import { z } from 'zod'
import { localeSchema } from './i18n.ts'
import { mobilePhoneSchema, optionalPhoneSchema } from './phone.ts'
import { orgTypeSchema, roleSchema } from './rbac.ts'

export const ENTITY_STATUSES = ['active', 'suspended'] as const
export type EntityStatus = (typeof ENTITY_STATUSES)[number]
export const entityStatusSchema = z.enum(ENTITY_STATUSES)

export const MIN_PASSWORD_LENGTH = 8
export const MAX_PASSWORD_LENGTH = 128

/**
 * Deliberately permissive. The audience is shop owners, and complexity rules push
 * people towards writing passwords on the counter. Length plus rate limiting (D6)
 * carries more weight than a symbol requirement.
 */
export const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, `Use at least ${MIN_PASSWORD_LENGTH} characters.`)
  .max(MAX_PASSWORD_LENGTH)

export const personNameSchema = z.string().trim().min(2, 'Enter a name.').max(80)
export const optionalEmailSchema = z
  .union([z.literal(''), z.email('Enter a valid email address.')])
  .transform((v) => (v === '' ? null : v))
  .nullish()

// ---------------------------------------------------------------------------
// Organisations
// ---------------------------------------------------------------------------

export const orgSchema = z.object({
  id: z.string(),
  type: orgTypeSchema,
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  addressLine: z.string().nullable(),
  city: z.string().nullable(),
  panVat: z.string().nullable(),
  notes: z.string().nullable(),
  status: entityStatusSchema,
  createdAt: z.number().int(),
})
export type Org = z.infer<typeof orgSchema>

export const createOrgSchema = z.object({
  type: z.enum(['seller', 'buyer']),
  name: z.string().trim().min(2, 'Enter the business name.').max(120),
  phone: optionalPhoneSchema,
  email: optionalEmailSchema,
  addressLine: z.string().trim().max(200).nullish(),
  city: z.string().trim().max(80).nullish(),
  /** Nepali PAN/VAT registration number: 9 digits. */
  panVat: z
    .union([z.literal(''), z.string().regex(/^\d{9}$/, 'PAN/VAT is 9 digits.')])
    .transform((v) => (v === '' ? null : v))
    .nullish(),
  notes: z.string().trim().max(1000).nullish(),
})
export type CreateOrgInput = z.infer<typeof createOrgSchema>

export const updateOrgSchema = createOrgSchema.omit({ type: true }).partial().extend({
  status: entityStatusSchema.optional(),
})
export type UpdateOrgInput = z.infer<typeof updateOrgSchema>

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export const userSchema = z.object({
  id: z.string(),
  orgId: z.string().nullable(),
  orgName: z.string().nullable(),
  role: roleSchema,
  name: z.string(),
  phone: z.string(),
  email: z.string().nullable(),
  status: entityStatusSchema,
  mustChangePassword: z.boolean(),
  lastLoginAt: z.number().int().nullable(),
  createdAt: z.number().int(),
})
export type User = z.infer<typeof userSchema>

export const createUserSchema = z.object({
  role: roleSchema,
  /** Required for seller/buyer roles; must be null for admin-group roles. */
  orgId: z.string().nullish(),
  name: personNameSchema,
  phone: mobilePhoneSchema,
  email: optionalEmailSchema,
  /** Omit to have the server generate a readable temporary password. */
  password: passwordSchema.optional(),
})
export type CreateUserInput = z.infer<typeof createUserSchema>

export const updateUserSchema = z.object({
  name: personNameSchema.optional(),
  phone: mobilePhoneSchema.optional(),
  email: optionalEmailSchema,
  status: entityStatusSchema.optional(),
})
export type UpdateUserInput = z.infer<typeof updateUserSchema>

/**
 * Admin-mediated reset (D2/D5): the server returns a temporary password once, in
 * the response body, for the admin to pass on by voice or WhatsApp. It is never
 * stored in readable form and never shown again.
 */
export const resetPasswordResultSchema = z.object({
  userId: z.string(),
  temporaryPassword: z.string(),
})
export type ResetPasswordResult = z.infer<typeof resetPasswordResultSchema>

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export const loginSchema = z.object({
  phone: mobilePhoneSchema,
  password: z.string().min(1, 'Enter your password.').max(MAX_PASSWORD_LENGTH),
})
export type LoginInput = z.infer<typeof loginSchema>

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password.'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'The two passwords do not match.',
    path: ['confirmPassword'],
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    message: 'Choose a password different from your current one.',
    path: ['newPassword'],
  })
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>

/**
 * A walkthrough this person has already been through.
 *
 * The names are the browser's — which tour has which steps is a question about
 * screens, and screens are not something the server has an opinion on. What the
 * server holds is the fact that somebody has seen one, so the shape is constrained
 * and the vocabulary is not.
 *
 * No message on the rule, deliberately: nobody types this, so a failure here is a
 * malformed request rather than something to explain to a reader. The table in
 * `validation-messages.ts` is for the sentences people actually see.
 */
export const tourIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(48)
  .regex(/^[a-z0-9-]+$/)
export const toursSeenSchema = z.array(tourIdSchema).max(40)

export const sessionUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  phone: z.string(),
  email: z.string().nullable(),
  role: roleSchema,
  orgId: z.string().nullable(),
  orgName: z.string().nullable(),
  orgType: orgTypeSchema.nullable(),
  permissions: z.array(z.string()),
  mustChangePassword: z.boolean(),
  /** Per-user preference that reveals the power-user surfaces. */
  advancedMode: z.boolean(),
  /** Language this person reads the app in. Follows them to any device they sign in on. */
  locale: localeSchema,
  /**
   * Walkthroughs already taken. Empty on a brand-new account, which is what makes
   * the welcome open by itself exactly once — on whichever device they first use.
   */
  toursSeen: z.array(z.string()),
})
export type SessionUser = z.infer<typeof sessionUserSchema>
